/**
 * Security View - Unified security controls
 * WebAuthn passkey authentication + Master Password Keys
 */

import { useState } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Fingerprint,
  Lock,
  AlertTriangle,
  Loader2,
  CheckCircle,
  RefreshCw,
} from "lucide-react";
import * as Tabs from "@radix-ui/react-tabs";
import * as Switch from "@radix-ui/react-switch";
import { cn } from "@/lib/utils";
import { useDashboardStore } from "@/store";
import { authService } from "@/services/authService";
import { convexService } from "@/services/convexService";

type SecurityTab = "overview" | "auth" | "sessions" | "audit";

export function SecurityView() {
  const { authLevel, settings, updateSettings, addLogEntry } = useDashboardStore();
  const [activeTab, setActiveTab] = useState<SecurityTab>("overview");
  const [isTestingConvex, setIsTestingConvex] = useState(false);
  const [convexStatus, setConvexStatus] = useState<"unknown" | "connected" | "disconnected">("unknown");

  const testConvexConnection = async () => {
    setIsTestingConvex(true);
    setConvexStatus("unknown");

    try {
      const success = await convexService.healthCheck();
      setConvexStatus(success ? "connected" : "disconnected");
    } catch {
      setConvexStatus("disconnected");
    } finally {
      setIsTestingConvex(false);
    }
  };

  const tabs: { id: SecurityTab; label: string; icon: typeof Shield; description: string }[] = [
    {
      id: "overview",
      label: "Overview",
      icon: Shield,
      description: "Current security status and settings",
    },
    {
      id: "auth",
      label: "Authentication",
      icon: Lock,
      description: "Manage passwords and passkeys",
    },
    {
      id: "sessions",
      label: "Active Sessions",
      icon: Fingerprint,
      description: "View and manage active sessions",
    },
    {
      id: "audit",
      label: "Audit Log",
      icon: AlertTriangle,
      description: "View security-related actions",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--primary))] glow-green">
            <Shield className="h-6 w-6 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Security Center</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Manage authentication, sessions, and security settings
            </p>
          </div>
        </div>

        {/* Auth Level Badge */}
        <div className={cn(
          "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium",
          authLevel === "creator"
            ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
            : authLevel === "master"
              ? "bg-green-500/20 text-green-300 border border-green-500/30"
              : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
        )}>
          {authLevel === "creator"
            ? <>
                <Fingerprint className="h-4 w-4" />
                Creator Authority
              </>
            : authLevel === "master"
              ? <>
                  <Shield className="h-4 w-4" />
                  Master Access
                </>
              : "Not Authenticated"
          }
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs.Root value={activeTab} onValueChange={(v) => setActiveTab(v as SecurityTab)}>
        <Tabs.List className="flex gap-2 border-b border-[hsl(var(--border))] pb-2">
          {tabs.map((tab) => (
            <Tabs.Trigger
              key={tab.id}
              value={tab.id}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors cursor-pointer",
                activeTab === tab.id
                  ? "bg-[hsl(var(--primary))] text-black"
                  : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div className="mt-6">
          <Tabs.Content value="overview">
            <SecurityOverview
              authLevel={authLevel}
              convexStatus={convexStatus}
              isTestingConvex={isTestingConvex}
              onTestConvex={testConvexConnection}
            />
          </Tabs.Content>
          <Tabs.Content value="auth">
            <AuthenticationSettings />
          </Tabs.Content>
          <Tabs.Content value="sessions">
            <SessionManagement />
          </Tabs.Content>
          <Tabs.Content value="audit">
            <AuditLog />
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  );
}

function SecurityOverview({
  authLevel,
  convexStatus,
  isTestingConvex,
  onTestConvex,
}: {
  authLevel: string;
  convexStatus: "unknown" | "connected" | "disconnected";
  isTestingConvex: boolean;
  onTestConvex: () => void;
}) {
  const { logs } = useDashboardStore();

  const securityEvents = logs
    .filter((log) => log.category === "security")
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Auth Status */}
      <div className={cn(
        "rounded-xl border p-6",
        authLevel === "creator"
          ? "border-purple-500/30 bg-purple-500/10"
          : authLevel === "master"
            ? "border-green-500/30 bg-green-500/10"
            : "border-[hsl(var(--border))] bg-[hsl(var(--muted)/.5)]"
      )}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1">Authentication Status</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {authLevel === "creator"
                ? "You have absolute authority. All protocols are bypassed."
                : authLevel === "master"
                  ? "You have master access with limited override capabilities."
                  : "Not authenticated. Please login to access restricted features."
              }
            </p>
          </div>
          <div className={cn(
            "flex h-12 w-12 items-center justify-center rounded-full",
            authLevel === "creator"
              ? "bg-purple-500/20"
              : authLevel === "master"
                ? "bg-green-500/20"
                : "bg-[hsl(var(--muted))]"
          )}>
            {authLevel === "creator"
              ? <Fingerprint className="h-6 w-6 text-purple-400" />
              : authLevel === "master"
                ? <Shield className="h-6 w-6 text-green-400" />
                : <Lock className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
            }
          </div>
        </div>
      </div>

      {/* Convex Connection */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg mb-1">Convex Backend</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Secure authentication via cloud backend
            </p>
          </div>
          <button
            onClick={onTestConvex}
            disabled={isTestingConvex}
            className={cn(
              "rounded-lg px-3 py-1.5 text-sm flex items-center gap-2 cursor-pointer",
              isTestingConvex
                ? "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                : "bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary)/.9)]"
            )}
          >
            {isTestingConvex ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Test Connection
              </>
            )}
          </button>
        </div>

        {convexStatus !== "unknown" && (
          <div className={cn(
            "flex items-center gap-2 rounded-lg p-3",
            convexStatus === "connected"
              ? "bg-green-500/20 text-green-400"
              : "bg-red-500/20 text-red-400"
          )}>
            {convexStatus === "connected" ? (
              <>
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Connected and authenticated</span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">Unable to connect to Convex</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Recent Security Events */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <h3 className="font-semibold text-lg mb-3">Recent Security Events</h3>
        <div className="space-y-2">
          {securityEvents.length > 0 ? (
            securityEvents.map((log) => (
              <div
                key={log.id}
                className={cn(
                  "flex items-center justify-between rounded-lg p-3",
                  log.severity === "critical"
                    ? "bg-red-500/10"
                    : log.severity === "warning"
                      ? "bg-yellow-500/10"
                      : "bg-[hsl(var(--muted)/.5)]"
                )}
              >
                <div>
                  <p className="text-sm font-medium">{log.action}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {log.details}
                  </p>
                </div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
            ))
          ) : (
            <p className="text-center text-sm text-[hsl(var(--muted-foreground))] py-4">
              No recent security events
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function AuthenticationSettings() {
  const { settings, updateSettings } = useDashboardStore();

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <h3 className="text-lg font-semibold mb-4">Password Keys</h3>
        <div className="space-y-4">
          <div className="rounded-lg bg-[hsl(var(--muted)/.5)] p-4">
            <h4 className="font-medium text-sm mb-2 text-purple-400">Creator Key</h4>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Provides absolute authority over the system. Use with caution.
            </p>
          </div>

          <div className="rounded-lg bg-[hsl(var(--muted)/.5)] p-4">
            <h4 className="font-medium text-sm mb-2 text-green-400">Master Key</h4>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Provides limited override capabilities. For emergency use.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <h3 className="text-lg font-semibold mb-4">Session Management</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-[hsl(var(--muted-foreground))]">Session Timeout (hours)</label>
            <input
              type="number"
              value={settings.sessionTimeout}
              onChange={(e) => updateSettings({ sessionTimeout: parseInt(e.target.value) || 4 })}
              min="1"
              max="24"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-sm text-[hsl(var(--muted-foreground))]">Require Re-authentication (hours)</label>
            <input
              type="number"
              value={settings.requireReauthAfter}
              onChange={(e) => updateSettings({ requireReauthAfter: parseInt(e.target.value) || 12 })}
              min="1"
              max="168"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionManagement() {
  const { logs } = useDashboardStore();
  const authEvents = logs.filter((log) => log.category === "auth").slice(0, 20);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <h3 className="text-lg font-semibold mb-2">Authentication History</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
          Track all login and logout events
        </p>

        <div className="space-y-2">
          {authEvents.length > 0 ? (
            authEvents.map((log) => (
              <div
                key={log.id}
                className="flex items-center justify-between rounded-lg bg-[hsl(var(--muted)/.5)] p-3"
              >
                <div>
                  <p className="text-sm font-medium">{log.action}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {log.details}
                  </p>
                </div>
                <div className="text-right">
                  <span className={cn(
                    "rounded px-2 py-0.5 text-xs",
                    log.severity === "critical"
                      ? "bg-red-500/20 text-red-400"
                      : log.severity === "warning"
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-green-500/20 text-green-400"
                  )}>
                    {log.severity}
                  </span>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {new Date(log.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-sm text-[hsl(var(--muted-foreground))] py-8">
              No authentication events recorded
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditLog() {
  const { logs, clearLogs } = useDashboardStore();
  const securityLogs = logs.filter(
    (log) =>
      log.category === "security" ||
      log.category === "auth" ||
      log.category === "override"
  ).slice(0, 50);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Showing {securityLogs.length} security events
        </p>
        <button
          onClick={clearLogs}
          className="text-sm text-red-400 hover:text-red-300 cursor-pointer"
        >
          Clear Logs
        </button>
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {securityLogs.length > 0 ? (
            securityLogs.map((log) => (
              <div
                key={log.id}
                className={cn(
                  "flex items-start gap-3 rounded-lg p-3",
                  log.severity === "critical"
                    ? "bg-red-500/10 border-l-2 border-red-500"
                    : log.severity === "warning"
                      ? "bg-yellow-500/10 border-l-2 border-yellow-500"
                      : "bg-[hsl(var(--muted)/.5)] border-l-2 border-green-500"
                )}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium">{log.action}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      {log.category}
                    </span>
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {log.details}
                  </p>
                  {log.source && (
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      Source: {log.source}
                    </p>
                  )}
                </div>
                <span className="text-xs text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
            ))
          ) : (
            <p className="text-center text-sm text-[hsl(var(--muted-foreground))] py-8">
              No security events recorded
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleSetting({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between py-2">
      <div className="flex-1">
        <label className="text-sm font-medium">{label}</label>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
      </div>
      <Switch.Root
        checked={checked}
        onCheckedChange={onChange}
        className={cn(
          "h-6 w-11 rounded-full transition-colors cursor-pointer",
          checked ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]"
        )}
      >
        <Switch.Thumb className="block h-5 w-5 rounded-full bg-white transition-transform data-[state=checked]:translate-x-5" />
      </Switch.Root>
    </div>
  );
}
